import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Archive, RotateCcw, GitBranch } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { formatDateOnly } from "@/lib/dates";

export const TL_STATUS_COLOR = {
  draft: "text-zinc-400", active: "text-emerald-400", closed: "text-sky-400",
};

function Stat({ label, value, color, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`font-tactical font-black text-2xl ${color || "text-white"}`}>{value ?? 0}</div>
      <div className="label-caps text-zinc-500 mt-1">{label}</div>
    </Card>
  );
}

export default function TimelineBuilder() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = (user?.roles || []).some((r) => ["editor", "super_admin"].includes(r));
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", topic: "", timeline_type: "", status: "", region: "", geo_scope: "" });
  const [tab, setTab] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  function blank() {
    return { title: "", summary: "", topic: "", timeline_type: "", geo_scope: "", region: "", start_date: "", end_date: "", analyst_notes: "", tags: "" };
  }

  const loadMeta = () => {
    api.get("/timeline/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/timeline/dashboard").then((r) => setDash(r.data)).catch(() => {});
  };
  const loadRows = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (tab === "archived") params.archived = true;
    setLoading(true); setRows([]);
    api.get("/timeline/timelines", { params }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, tab]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadRows(); }, [loadRows]);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form };
      payload.tags = payload.tags ? payload.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/timeline/timelines", payload);
      toast.success("Timeline created");
      setOpen(false); setForm(blank()); loadMeta();
      nav(`/command-center/timelines/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runAction = async () => {
    if (busy || !action) return;
    setBusy(true);
    try {
      if (action.mode === "archive") await api.delete(`/timeline/timelines/${action.tl.id}`);
      else await api.post(`/timeline/timelines/${action.tl.id}/restore`);
      toast.success(action.mode === "archive" ? "Timeline archived" : "Timeline restored");
      setAction(null); loadRows(); loadMeta();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <PageHeader title="Timeline Builder" subtitle="Construct evidence-backed chronological timelines — private workspace.">
        <Btn data-testid="tl-new-btn" disabled={!meta} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Timeline</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Total" value={dash?.total} testid="tl-stat-total" />
        <Stat label="Active" value={dash?.active} color="text-emerald-400" testid="tl-stat-active" />
        <Stat label="Draft" value={dash?.draft} color="text-zinc-400" testid="tl-stat-draft" />
        <Stat label="Closed" value={dash?.closed} color="text-sky-400" testid="tl-stat-closed" />
        <Stat label="Events" value={dash?.total_events} color="text-white" testid="tl-stat-events" />
        <Stat label="Unverified Events" value={dash?.unverified_events} color="text-amber-400" testid="tl-stat-unverified" />
      </div>

      <Card className="p-4 mb-6">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Search"><input data-testid="tl-filter-q" className={inputCls} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Title, summary, tags" /></Field>
          <Field label="Type"><select data-testid="tl-filter-type" className={inputCls} value={filters.timeline_type} onChange={(e) => setFilters({ ...filters, timeline_type: e.target.value })}><option value="">All</option>{(meta?.timeline_types || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
          <Field label="Status"><select data-testid="tl-filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All</option>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Topic"><select data-testid="tl-filter-topic" className={inputCls} value={filters.topic} onChange={(e) => setFilters({ ...filters, topic: e.target.value })}><option value="">All</option>{(meta?.topics || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
          <Field label="Region"><select data-testid="tl-filter-region" className={inputCls} value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}><option value="">All</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
          <Field label="Scope"><select data-testid="tl-filter-scope" className={inputCls} value={filters.geo_scope} onChange={(e) => setFilters({ ...filters, geo_scope: e.target.value })}><option value="">All</option>{(meta?.geo_scopes || []).map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
        </div>
      </Card>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`tl-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{t}</button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm" data-testid="tl-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Timeline</th><th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Topic / Region</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Events</th><th className="px-4 py-2.5">Span</th>
              <th className="px-4 py-2.5">{tab === "archived" ? "Archived" : "Updated"}</th>
              {canManage && <th className="px-4 py-2.5">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-6 text-zinc-600" data-testid="tl-loading">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-6 text-zinc-600" data-testid="tl-empty">{tab === "archived" ? "No archived timelines." : "No timelines match."}</td></tr>}
            {!loading && rows.map((t) => (
              <tr key={t.id} className={`hover:bg-zinc-900/50 ${tab === "active" ? "cursor-pointer" : ""}`} data-testid={`tl-row-${t.id}`} onClick={() => tab === "active" && nav(`/command-center/timelines/${t.id}`)}>
                <td className="px-4 py-2.5 max-w-sm"><div className="font-medium truncate">{t.title}</div>{t.tags?.length > 0 && <span className="text-[0.65rem] text-fuchsia-400">{t.tags.join(", ")}</span>}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{t.timeline_type || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{[t.topic, t.region].filter(Boolean).join(" / ") || "—"}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${TL_STATUS_COLOR[t.status] || "text-zinc-400"}`}>{t.status}</td>
                <td className="px-4 py-2.5 text-zinc-300 text-xs" data-testid={`tl-events-${t.id}`}>{t.event_count ?? 0}</td>
                <td className="px-4 py-2.5 text-zinc-500 text-xs">{[formatDateOnly(t.start_date), formatDateOnly(t.end_date)].filter((x) => x && x !== "—").join(" → ") || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-600 text-xs">{tab === "archived" ? (t.deleted_at ? new Date(t.deleted_at).toLocaleDateString() : "—") : (t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—")}</td>
                {canManage && (
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {tab === "active" ? (
                      <button data-testid={`tl-archive-${t.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "archive", tl: t }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    ) : (
                      <button data-testid={`tl-restore-${t.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "restore", tl: t }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!action} onClose={() => { if (!busy) setAction(null); }} title={action?.mode === "archive" ? "Archive timeline" : "Restore timeline"}>
        {action && (
          <div className="space-y-4" data-testid="tl-action-confirm">
            <p className="text-sm text-zinc-400">{action.mode === "archive" ? "Archive this timeline? It is hidden from the active list but preserved with all events, evidence links and audit trail, and can be restored anytime." : "Restore this timeline? All events, links and history are preserved."}</p>
            <div className="text-sm font-mono-data break-words" data-testid="tl-action-title">{action.tl.title}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="tl-action-cancel" disabled={busy} onClick={() => setAction(null)}>Cancel</Btn>
              <Btn data-testid="tl-action-confirm-btn" disabled={busy} onClick={runAction} className="flex-1 justify-center">{busy ? "Working…" : action.mode === "archive" ? "Archive" : "Restore"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Timeline" wide>
        <form onSubmit={create} className="space-y-4">
          <Field label="Timeline title"><input data-testid="tl-title" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Summary"><textarea data-testid="tl-summary" className={inputCls} rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Type"><select data-testid="tl-type" className={inputCls} value={form.timeline_type} onChange={(e) => setForm({ ...form, timeline_type: e.target.value })}><option value="">—</option>{(meta?.timeline_types || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Topic"><select data-testid="tl-topic" className={inputCls} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}><option value="">—</option>{(meta?.topics || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Scope"><select data-testid="tl-scope" className={inputCls} value={form.geo_scope} onChange={(e) => setForm({ ...form, geo_scope: e.target.value })}><option value="">—</option>{(meta?.geo_scopes || []).map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Region"><select data-testid="tl-region" className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="">—</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="Start date"><input type="date" data-testid="tl-start" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="End date"><input type="date" data-testid="tl-end" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          </div>
          <Field label="Analyst notes"><textarea data-testid="tl-notes" className={inputCls} rows={2} value={form.analyst_notes} onChange={(e) => setForm({ ...form, analyst_notes: e.target.value })} /></Field>
          <Field label="Tags (comma-separated)"><input data-testid="tl-tags" className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Field>
          <Btn data-testid="tl-save-btn" type="submit" disabled={saving} className="w-full justify-center"><GitBranch className="h-4 w-4" /> {saving ? "Creating…" : "Create Timeline"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
